import {
  closestCenter,
  CollisionDetection,
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import cn from 'classnames';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useBlocker, useLocation, useNavigate } from 'react-router';

import { templatesApi } from '@/shared/api/transcriptor/templatesApi';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { buildTemplatesUrl, debug } from '@/shared/lib';
import { SlotSelectionSnapshot, toastCaller } from '@/shared/ui';

import { ModalID, modalModel } from '@/entities/modal';
import { organizationModel } from '@/entities/organization';
import {
  buildChecklistRows,
  ChecklistPanel,
  createTemplatesNavigationState,
  DocumentSectionSpec,
  DocumentTemplate,
  DocumentTemplateParseResponse,
  DocumentType,
  EncounterSchema,
  getDefaultSectionTemplateType,
  getDocumentTypeRules,
  hasCompletedChecklist,
  getInclusionChangeError,
  getTemplateInclusion,
  isProtocolTemplate,
  normalizeDocumentType,
  normalizeInclusionValue,
  resolveTemplatesBackPath,
  SectionOptionSchema,
  stripUnsupportedOptions,
  templatesModel,
  VersionStatus,
} from '@/entities/templates';

import {
  OPTION_DROP_ID_SEPARATOR,
  PALETTE_DRAG_TYPES,
  templateEditorMessages,
} from '../../config';
import type {
  AiInstructionsTarget,
  AutoFocusTarget,
  DropSide,
  PaletteDragType,
  ImportedTemplateJson,
  ImportStrategy,
  OptionTarget,
  TemplateDetailsValues,
  TemplateSettingsDraft,
} from '../../config';
import {
  addLegacyAiOption,
  areIdListsEqual,
  buildSchemaDocuments,
  cloneSystemTemplateForEditing,
  createAiOption,
  deriveTabTitle,
  diffSchemaMembership,
  extractSlotNames,
  findLastSectionWithStrictOptions,
  generateSectionCode,
  generateTemplateCode,
  generateUniqueTemplateCode,
  getAiOptionIndex,
  getLastStrictOptionIndex,
  getOptionTargetFromDropId,
  getSectionIds,
  getSectionTemplateType,
  getStrictInsertIndex,
  getTemplateSchemaIds,
  isAiOption,
  isAiSlotName,
  mapParsedSections,
  remapFocusedOption,
  renameSlotInOption,
  resolvePaletteDrop,
  toggleAiSlotName,
  trimFields,
  updateOption,
  useFormDataHistory,
} from '../../lib';
import { AiInstructionsModal } from '../AiInstructionsModal';
import { EditorButton } from '../EditorButton';
import { EditorHeader } from '../EditorHeader';
import { EditorHistory } from '../EditorHistory';
import { EditorToolbar } from '../EditorToolbar';
import { ImportTemplateModal } from '../ImportTemplateModal';
import { SectionNav } from '../SectionNav';
import { SlotEditorPopup, SlotEditorSaveResult } from '../SlotEditorPopup';
import { SourceDocumentDialog } from '../SourceDocumentDialog';
import { TemplateDetailsModal } from '../TemplateDetailsModal';
import { TemplateMetaLine } from '../TemplateMetaLine';
import { TemplateSectionsEditor } from '../TemplateSectionsEditor';

import styles from './DocumentTemplateForm.module.scss';

type FormData = Omit<
  DocumentTemplate,
  | 'id'
  | 'series_id'
  | 'version'
  | 'is_system'
  | 'created_at'
  | 'created_by'
  | 'catalog_published'
>;

type DuplicateTemplateConflictField = 'code' | 'name';

type PendingSlotInsertion = SlotSelectionSnapshot & {
  sectionIndex: number;
  optionIndex: number;
  slotName: string;
};

type SlotEditState = {
  sectionIndex: number;
  optionIndex: number;
  slotName: string;
};

type PersistedTemplateState = {
  id?: string;
  series_id?: string;
  organization_id?: string | null;
  status?: VersionStatus;
  code?: string;
  type?: DocumentType;
};

const getPersistedTemplateState = (
  template?: Pick<
    DocumentTemplate,
    'id' | 'series_id' | 'organization_id' | 'status' | 'code' | 'type'
  >,
): PersistedTemplateState =>
  template
    ? {
        id: template.id,
        series_id: template.series_id,
        organization_id: template.organization_id,
        status: template.status,
        code: template.code || undefined,
        type: normalizeDocumentType(template.type),
      }
    : {};

type PersistTemplateOptions = {
  silent?: boolean;
};

type ValidationIssue = {
  message: string;
  sectionIndex?: number;
};

// Sticky toolbar занимает ~24px gap + ~76px тулбар + 12px breathing над секцией.
const SECTION_SCROLL_TOP_INSET = 112;

const getCssPixelValue = (value: string) => {
  const parsedValue = Number.parseFloat(value);

  return Number.isNaN(parsedValue) ? 0 : parsedValue;
};

const getDocumentHeaderOffset = () => {
  const rootStyles = window.getComputedStyle(document.documentElement);
  const actualHeaderHeight = getCssPixelValue(
    rootStyles.getPropertyValue('--actualHeaderHeight'),
  );

  if (actualHeaderHeight > 0) {
    return actualHeaderHeight;
  }

  return getCssPixelValue(rootStyles.getPropertyValue('--headerHeight'));
};

const getScrollContainerViewportTop = (scrollContainer: HTMLElement) => {
  if (scrollContainer === document.scrollingElement) {
    return 0;
  }

  return scrollContainer.getBoundingClientRect().top;
};

const getScrollTopInset = (scrollContainer: HTMLElement) => {
  if (scrollContainer === document.scrollingElement) {
    return getDocumentHeaderOffset() + SECTION_SCROLL_TOP_INSET;
  }

  return SECTION_SCROLL_TOP_INSET;
};

const isVerticallyScrollable = (element: HTMLElement) => {
  const { overflowY } = window.getComputedStyle(element);
  const canScroll = ['auto', 'scroll', 'overlay'].includes(overflowY);

  return canScroll && element.scrollHeight > element.clientHeight;
};

const getSectionScrollContainer = (
  targetSection: HTMLElement,
  fallbackContainer: HTMLElement,
) => {
  let current: HTMLElement | null = targetSection;

  while (current) {
    if (isVerticallyScrollable(current)) {
      return current;
    }

    current = current.parentElement;
  }

  const documentScroller = document.scrollingElement;
  if (
    documentScroller instanceof HTMLElement &&
    documentScroller.scrollHeight > documentScroller.clientHeight
  ) {
    return documentScroller;
  }

  return fallbackContainer;
};

type DocumentTemplateFormProps = {
  className?: string;
  initialSchemaId?: string | null;
  initialSettings?: TemplateSettingsDraft;
  schemaLanguage?: string;
  specialtyOptions?: EncounterSchema[];
  template?: DocumentTemplate;
  onSaveComplete?: (templateId: string) => void;
};

const normalizeOptionalText = (value?: string | null): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

const createEmptySection = (
  documentType?: DocumentType,
): DocumentSectionSpec => ({
  code: generateSectionCode(),
  title: '',
  editor_type: 'section',
  processing_type: 'STANDARD',
  template_type: getDefaultSectionTemplateType(documentType),
});

const createNewSection = (documentType?: DocumentType): DocumentSectionSpec => {
  const section = createEmptySection(documentType);

  return getDocumentTypeRules(documentType).allowOptions
    ? { ...section, options: [createAiOption()] }
    : section;
};

const sanitizeSectionForSubmit = (
  section: DocumentSectionSpec,
  documentType: DocumentType,
): DocumentSectionSpec => {
  // Safety net only: sections enter the form already stripped by
  // stripUnsupportedOptions, so nothing visible is dropped here.
  const options = getDocumentTypeRules(documentType).allowOptions
    ? section.options
        ?.map((option) => {
          const slots = Object.entries(option.slots ?? {}).reduce<
            Record<string, string[]>
          >((acc, [key, values]) => {
            const validValues = values
              .map((value) => value.trim())
              .filter(Boolean);
            if (validValues.length > 0 || isAiSlotName(key)) {
              acc[key] = validValues;
            }
            return acc;
          }, {});

          return {
            ...option,
            text: option.text.trim(),
            instructions: normalizeOptionalText(option.instructions),
            slot_instructions: option.slot_instructions,
            slots: Object.keys(slots).length > 0 ? slots : undefined,
          };
        })
        .filter((option) => option.text.length > 0)
    : undefined;

  return {
    ...section,
    code: section.code.trim(),
    title: section.title.trim(),
    category: normalizeOptionalText(section.category),
    editor_type: 'section',
    instructions: normalizeOptionalText(section.instructions),
    processing_type: section.processing_type ?? 'STANDARD',
    template_type: getSectionTemplateType(documentType, options),
    options: options && options.length > 0 ? options : undefined,
  };
};

const createEmptyFormData = (language = 'en'): FormData => ({
  code: '',
  language,
  status: 'draft' as VersionStatus,
  type: 'PROTOCOL',
  name: '',
  tab_title: '',
  description: '',
  sections: [],
});

const createFormDataFromInitialSettings = (
  settings: TemplateSettingsDraft,
): FormData => ({
  ...createEmptyFormData(settings.language),
  code: settings.code,
  language: settings.language,
  status: settings.status,
  type: settings.documentType ?? 'PROTOCOL',
  name: settings.name,
  tab_title: settings.tabTitle,
  description: settings.description || undefined,
});

const createFormDataFromTemplate = (template: DocumentTemplate): FormData => {
  const type = normalizeDocumentType(template.type);

  return {
    code: template.code,
    language: template.language,
    status: template.status,
    type,
    name: template.name,
    tab_title: template.tab_title,
    description: template.description,
    organization_id: template.organization_id,
    sections: stripUnsupportedOptions(template.sections, type).sections.map(
      (section) => addLegacyAiOption({ ...section }),
    ),
    source_document: template.source_document,
  };
};

const getErrorMessage = (error: unknown): string | undefined => {
  if (error instanceof Error) {
    return error.message || undefined;
  }

  if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message || undefined;
  }

  return undefined;
};

const getDuplicateTemplateSaveConflictFields = (
  error: unknown,
): DuplicateTemplateConflictField[] => {
  const message = getErrorMessage(error);

  if (
    !message?.includes('DocumentTemplate with') ||
    !message.includes('already exists')
  ) {
    return [];
  }

  const fields: DuplicateTemplateConflictField[] = [];

  if (message.includes('code=')) {
    fields.push('code');
  }

  if (message.includes('name=')) {
    fields.push('name');
  }

  return fields;
};

const getDropSide = (event: DragMoveEvent | DragEndEvent): DropSide => {
  const overRect = event.over?.rect;

  if (!overRect || !(event.activatorEvent instanceof PointerEvent)) {
    return 'after';
  }

  const pointerY = event.activatorEvent.clientY + (event.delta?.y ?? 0);

  return pointerY < overRect.top + overRect.height / 2 ? 'before' : 'after';
};

const getNextSlotName = (option: SectionOptionSchema): string => {
  const occupiedNames = new Set([
    ...extractSlotNames(option.text),
    ...Object.keys(option.slots ?? {}),
  ]);

  let nextNumber = 1;
  while (occupiedNames.has(String(nextNumber))) {
    nextNumber++;
  }

  return String(nextNumber);
};

const appendSlotToken = (text: string, slotName: string): string => {
  const slotToken = `{{${slotName}}}`;

  if (text.trim().length === 0) {
    return slotToken;
  }

  return /\s$/.test(text) ? `${text}${slotToken}` : `${text} ${slotToken}`;
};

const replaceTextRangeWithSlot = (
  text: string,
  slotName: string,
  start: number,
  end: number,
): string => {
  const normalizedStart = Math.max(Math.min(start, end), 0);
  const normalizedEnd = Math.max(start, end);

  return `${text.slice(0, normalizedStart)}{{${slotName}}}${text.slice(normalizedEnd)}`;
};

const isPendingSlot = (
  pending: PendingSlotInsertion | null,
  target: SlotEditState | null,
): pending is PendingSlotInsertion =>
  pending !== null &&
  target !== null &&
  pending.sectionIndex === target.sectionIndex &&
  pending.optionIndex === target.optionIndex &&
  pending.slotName === target.slotName;

const getAiInstructionsValue = (
  sections: DocumentSectionSpec[],
  target: AiInstructionsTarget | null,
): string => {
  if (!target) {
    return '';
  }

  const section = sections[target.sectionIndex];

  return target.kind === 'section'
    ? section?.instructions ?? ''
    : section?.options?.[target.optionIndex]?.instructions ?? '';
};

export const DocumentTemplateForm: FC<DocumentTemplateFormProps> = (props) => {
  const {
    className,
    initialSchemaId,
    initialSettings,
    schemaLanguage,
    specialtyOptions,
    template,
    onSaveComplete,
  } = props;

  const { formatMessage } = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const organizationId = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationID,
  );
  const templatesBackPath = resolveTemplatesBackPath(location.state);
  const templatesNavigationState = useMemo(
    () => createTemplatesNavigationState(templatesBackPath),
    [templatesBackPath],
  );
  const autoOpenImport = Boolean(
    (location.state as { autoOpenImport?: boolean } | null | undefined)
      ?.autoOpenImport,
  );

  const initialFormState = template
    ? createFormDataFromTemplate(template)
    : initialSettings
      ? createFormDataFromInitialSettings(initialSettings)
      : createEmptyFormData(schemaLanguage);

  // Form state
  const [formData, setFormData] = useState<FormData>(initialFormState);
  const [initialSnapshot, setInitialSnapshot] = useState(
    JSON.stringify(initialFormState),
  );
  const [isPersisting, setIsPersisting] = useState(false);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const initialFormDataRef = useRef<string>(initialSnapshot);
  const hydratedTemplateKeyRef = useRef<string | null>(null);
  const activeTemplateRef = useRef<PersistedTemplateState>(
    getPersistedTemplateState(template),
  );
  // The hidden tab title follows renames while it still equals the name it
  // was loaded with, so remember the name that came with formData.tab_title.
  const hydratedNameRef = useRef(template?.name ?? '');
  const pendingPersistedTemplateRef = useRef<{
    templateId: string;
    snapshot: string;
  } | null>(null);
  const isPersistingRef = useRef(false);
  const skipNavigationBlockRef = useRef(false);
  const lastAutoSaveErrorRef = useRef<string | null>(null);

  // UI state
  const [activeDragType, setActiveDragType] = useState<
    PaletteDragType | 'reorder' | null
  >(null);
  const [dropIndicatorId, setDropIndicatorId] = useState<string | null>(null);
  const [dropIndicatorSide, setDropIndicatorSide] = useState<DropSide>('after');
  const [autoFocusTarget, setAutoFocusTarget] =
    useState<AutoFocusTarget | null>(null);
  const [activeSectionIndex, setActiveSectionIndex] = useState<number | null>(
    0,
  );
  const [hasFocusedOption, setHasFocusedOption] = useState(false);
  // Kept after blur so toolbar actions still target the option the user left;
  // hasFocusedOption says whether that focus is live.
  const focusedOptionRef = useRef<OptionTarget | null>(null);
  const lastFocusedSectionRef = useRef<number | null>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const editorInsertSlotRef = useRef<((slotName: string) => void) | null>(null);
  const editorSelectionTextRef = useRef<SlotSelectionSnapshot | null>(null);
  // Pressing Add Field moves focus out of the editor and clears its selection
  // before the click fires, so the selection is captured on pointer down.
  const addFieldSelectionRef = useRef<SlotSelectionSnapshot | null>(null);
  // A selection turns into a slot only once its Edit Field modal is saved.
  const pendingSlotInsertionRef = useRef<PendingSlotInsertion | null>(null);
  const editorBodyRef = useRef<HTMLDivElement>(null);

  // Specialty membership is stored on the encounter schemas, not on the
  // template, so the selection lives outside formData. A new template is not
  // attached anywhere yet, hence the empty original list.
  const [selectedSchemaIds, setSelectedSchemaIds] = useState<string[]>(() =>
    template
      ? getTemplateSchemaIds({
          schemas: specialtyOptions ?? [],
          seriesId: template.series_id,
        })
      : initialSchemaId
        ? [initialSchemaId]
        : [],
  );
  const originalSchemaIdsRef = useRef<string[]>(
    template ? selectedSchemaIds : [],
  );
  const [initialSchemaIds, setInitialSchemaIds] = useState(selectedSchemaIds);
  const selectedSchemaIdsRef = useRef(selectedSchemaIds);
  selectedSchemaIdsRef.current = selectedSchemaIds;
  const initialSchemaIdsRef = useRef(initialSchemaIds);
  initialSchemaIdsRef.current = initialSchemaIds;
  const schemaMembershipKeyRef = useRef<string | null>(null);

  // Popups
  const [errorSectionIndex, setErrorSectionIndex] = useState<number | null>(
    null,
  );
  const [aiInstructionsTarget, setAiInstructionsTarget] =
    useState<AiInstructionsTarget | null>(null);
  const [slotEditState, setSlotEditState] = useState<SlotEditState | null>(
    null,
  );
  const [isSourceDocOpen, setIsSourceDocOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  // A fresh template needs its name before anything else — unless the import
  // modal opens first.
  const [isDetailsOpen, setIsDetailsOpen] = useState(
    () => !template && !autoOpenImport,
  );
  const [isChecklistOpen, setIsChecklistOpen] = useState(false);
  const checklistAnchorRef = useRef<HTMLDivElement>(null);

  // System template immutability
  const readOnly = template?.is_system === true;

  // Undo/Redo history (snapshots, debounced 600ms, max 50)
  const { canUndo, canRedo, undo, redo } = useFormDataHistory({
    formData,
    setFormData,
    enabled: !readOnly,
  });

  // History snapshots are re-parsed JSON, so the remembered option cannot be
  // followed by object identity and is dropped until an editor is focused.
  const handleUndo = useCallback(() => {
    focusedOptionRef.current = null;
    undo();
  }, [undo]);

  const handleRedo = useCallback(() => {
    focusedOptionRef.current = null;
    redo();
  }, [redo]);

  useEffect(() => {
    if (readOnly) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const meta = event.metaKey || event.ctrlKey;

      if (!meta || event.altKey) {
        return;
      }

      const key = event.key.toLowerCase();
      const isUndoCombo = key === 'z' && !event.shiftKey;
      const isRedoCombo = (key === 'z' && event.shiftKey) || key === 'y';

      if (!isUndoCombo && !isRedoCombo) {
        return;
      }

      const target = event.target as HTMLElement | null;
      const tag = target?.tagName.toLowerCase() ?? '';
      const isEditableTarget =
        tag === 'input' ||
        tag === 'textarea' ||
        target?.isContentEditable === true;

      if (isEditableTarget) {
        return;
      }

      event.preventDefault();
      if (isUndoCombo) {
        handleUndo();
      } else {
        handleRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleRedo, handleUndo, readOnly]);

  // Navigation blocking
  const isDirty =
    JSON.stringify(formData) !== initialSnapshot ||
    !areIdListsEqual(selectedSchemaIds, initialSchemaIds);
  const blocker = useBlocker(() => isDirty && !skipNavigationBlockRef.current);

  const syncInitialSnapshot = useCallback((snapshot: string) => {
    initialFormDataRef.current = snapshot;
    setInitialSnapshot(snapshot);
  }, []);

  const navigateWithoutPrompt = useCallback(
    (
      to: string,
      options: {
        replace?: boolean;
        state?: unknown;
      } = {},
    ) => {
      skipNavigationBlockRef.current = true;
      navigate(to, {
        replace: options.replace,
        state: options.state,
      });
      queueMicrotask(() => {
        skipNavigationBlockRef.current = false;
      });
    },
    [navigate],
  );

  const scrollToSection = useCallback((sectionIndex: number) => {
    requestAnimationFrame(() => {
      const editorBody = editorBodyRef.current;
      const targetSection = editorBody?.querySelector<HTMLElement>(
        `[data-section-anchor="${sectionIndex}"]`,
      );

      if (!editorBody || !targetSection) {
        return;
      }

      const scrollContainer = getSectionScrollContainer(
        targetSection,
        editorBody,
      );
      const containerTop = getScrollContainerViewportTop(scrollContainer);
      const topInset = getScrollTopInset(scrollContainer);
      const targetRect = targetSection.getBoundingClientRect();

      scrollContainer.scrollTo({
        top: Math.max(
          scrollContainer.scrollTop + targetRect.top - containerTop - topInset,
          0,
        ),
        behavior: 'smooth',
      });
    });
  }, []);

  const openUnsavedChangesConfirm = useCallback(() => {
    if (blocker.state !== 'blocked') {
      return;
    }

    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.Confirm,
        data: {
          title: formatMessage(templateEditorMessages.unsavedChangesTitle),
          description: formatMessage(
            templateEditorMessages.unsavedChangesDescription,
          ),
          okText: formatMessage(templateEditorMessages.unsavedChangesLeave),
          cancelText: formatMessage(templateEditorMessages.unsavedChangesStay),
          onConfirm: () => {
            dispatch(modalModel.actions.closeModal(ModalID.Confirm));
            blocker.proceed();
          },
          onCancel: () => {
            dispatch(modalModel.actions.closeModal(ModalID.Confirm));
            blocker.reset();
          },
        },
      }),
    );
  }, [blocker, dispatch, formatMessage]);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      openUnsavedChangesConfirm();
    }
  }, [blocker.state, openUnsavedChangesConfirm]);

  useEffect(() => {
    initialFormDataRef.current = initialSnapshot;
  }, [initialSnapshot]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Cleanup blur timeout
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const nextTemplateKey = template
      ? `${template.id}:${template.updated_at ?? ''}:${template.sections.length}`
      : null;

    if (template) {
      activeTemplateRef.current = getPersistedTemplateState(template);

      const pendingPersistedTemplate = pendingPersistedTemplateRef.current;
      if (pendingPersistedTemplate?.templateId === template.id) {
        hydratedTemplateKeyRef.current = nextTemplateKey;
        syncInitialSnapshot(pendingPersistedTemplate.snapshot);
        pendingPersistedTemplateRef.current = null;

        if (
          JSON.stringify(formDataRef.current) !==
          pendingPersistedTemplate.snapshot
        ) {
          return;
        }
      }

      if (hydratedTemplateKeyRef.current === nextTemplateKey) {
        return;
      }

      const nextFormData = createFormDataFromTemplate(template);
      hydratedTemplateKeyRef.current = nextTemplateKey;
      hydratedNameRef.current = template.name;
      setFormData(nextFormData);
      setActiveSectionIndex(nextFormData.sections.length > 0 ? 0 : null);
      lastFocusedSectionRef.current =
        nextFormData.sections.length > 0 ? 0 : null;
      focusedOptionRef.current = null;
      syncInitialSnapshot(JSON.stringify(nextFormData));
      return;
    }

    if (
      !hydratedTemplateKeyRef.current &&
      !initialSettings &&
      schemaLanguage &&
      formDataRef.current.name === '' &&
      formDataRef.current.code === '' &&
      formDataRef.current.sections.length === 0 &&
      formDataRef.current.language !== schemaLanguage
    ) {
      const nextFormData = createEmptyFormData(schemaLanguage);
      setFormData(nextFormData);
      setActiveSectionIndex(null);
      lastFocusedSectionRef.current = null;
      focusedOptionRef.current = null;
      syncInitialSnapshot(JSON.stringify(nextFormData));
    }
  }, [initialSettings, schemaLanguage, syncInitialSnapshot, template]);

  useEffect(() => {
    if (formData.sections.length === 0) {
      setActiveSectionIndex(null);
      lastFocusedSectionRef.current = null;
      return;
    }

    setActiveSectionIndex((prev) => {
      if (prev === null || prev >= formData.sections.length) {
        return 0;
      }

      return prev;
    });
  }, [formData.sections.length]);

  const handleAddSectionAt = useCallback(
    (insertIndex: number) => {
      setFormData((prev) => ({
        ...prev,
        sections: [
          ...prev.sections.slice(0, insertIndex),
          createNewSection(prev.type),
          ...prev.sections.slice(insertIndex),
        ],
      }));
      setActiveSectionIndex(insertIndex);
      lastFocusedSectionRef.current = insertIndex;
      setAutoFocusTarget({ sectionIndex: insertIndex });
      scrollToSection(insertIndex);
    },
    [scrollToSection],
  );

  const handleClickAddSection = useCallback(() => {
    const lastFocusedSectionIndex = lastFocusedSectionRef.current;
    const insertIndex =
      lastFocusedSectionIndex !== null
        ? lastFocusedSectionIndex + 1
        : formDataRef.current.sections.length;

    handleAddSectionAt(insertIndex);
  }, [handleAddSectionAt]);

  const handleAddOptionAt = useCallback(
    (sectionIndex: number, insertAt: number) => {
      const section = formDataRef.current.sections[sectionIndex];
      if (!section) {
        return;
      }

      const boundedInsertAt = getStrictInsertIndex({
        options: section.options ?? [],
        insertAt,
      });

      setFormData((prev) => ({
        ...prev,
        sections: prev.sections.map((item, index) => {
          if (index !== sectionIndex) {
            return item;
          }

          const options = item.options ?? [];
          const nextOptions = [
            ...options.slice(0, boundedInsertAt),
            { text: '' },
            ...options.slice(boundedInsertAt),
          ];

          return {
            ...item,
            options: nextOptions,
            template_type: getSectionTemplateType(prev.type, nextOptions),
          };
        }),
      }));
      setActiveSectionIndex(sectionIndex);
      lastFocusedSectionRef.current = sectionIndex;
      focusedOptionRef.current = { sectionIndex, optionIndex: boundedInsertAt };
      setHasFocusedOption(true);
      setAutoFocusTarget({
        sectionIndex,
        optionIndex: boundedInsertAt,
      });
      scrollToSection(sectionIndex);
    },
    [scrollToSection],
  );

  const appendSlotToOption = useCallback(
    (sectionIndex: number, optionIndex: number) => {
      const option =
        formDataRef.current.sections[sectionIndex]?.options?.[optionIndex];
      if (!option) {
        return;
      }

      const slotName = getNextSlotName(option);

      if (option.text.trim()) {
        setFormData((prev) => ({
          ...prev,
          sections: updateOption({
            sections: prev.sections,
            sectionIndex,
            optionIndex,
            update: (current) => ({
              ...current,
              text: appendSlotToken(current.text, slotName),
            }),
          }),
        }));
      } else {
        // An empty option has nothing to edit but the field itself, so the
        // field editor opens at once and the field is inserted on save.
        pendingSlotInsertionRef.current = {
          text: '',
          start: 0,
          end: option.text.length,
          sectionIndex,
          optionIndex,
          slotName,
        };
        setSlotEditState({ sectionIndex, optionIndex, slotName });
      }
      setActiveSectionIndex(sectionIndex);
      lastFocusedSectionRef.current = sectionIndex;
      focusedOptionRef.current = { sectionIndex, optionIndex };
      setHasFocusedOption(true);
      scrollToSection(sectionIndex);
    },
    [scrollToSection],
  );

  // ============================================================
  // DnD
  // ============================================================

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  /** ID-based collision detection (transcriptor pattern). */
  const dndCollisionDetection: CollisionDetection = useCallback((args) => {
    const activeId = String(args.active.id);
    const optionContainers = args.droppableContainers.filter((container) =>
      String(container.id).includes(OPTION_DROP_ID_SEPARATOR),
    );
    const sectionContainers = args.droppableContainers.filter(
      (container) => !String(container.id).includes(OPTION_DROP_ID_SEPARATOR),
    );

    if (activeId === PALETTE_DRAG_TYPES.FIELD) {
      const optionHit = pointerWithin({
        ...args,
        droppableContainers: optionContainers,
      });

      return optionHit.length > 0
        ? optionHit
        : closestCenter({ ...args, droppableContainers: optionContainers });
    }

    if (activeId === PALETTE_DRAG_TYPES.SECTION) {
      return closestCenter({
        ...args,
        droppableContainers: sectionContainers,
      });
    }

    if (
      activeId.includes(OPTION_DROP_ID_SEPARATOR) ||
      activeId === PALETTE_DRAG_TYPES.OPTION
    ) {
      const optionHit = pointerWithin({
        ...args,
        droppableContainers: optionContainers,
      });
      if (optionHit.length > 0) {
        return optionHit;
      }

      const sectionHit = pointerWithin({
        ...args,
        droppableContainers: sectionContainers,
      });
      if (sectionHit.length > 0) {
        return sectionHit;
      }

      return closestCenter(args);
    }

    // Section reorder → sections only
    return closestCenter({
      ...args,
      droppableContainers: sectionContainers,
    });
  }, []);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const activeId = String(event.active.id);

    setActiveDragType(
      activeId === PALETTE_DRAG_TYPES.SECTION ||
        activeId === PALETTE_DRAG_TYPES.OPTION ||
        activeId === PALETTE_DRAG_TYPES.FIELD
        ? activeId
        : 'reorder',
    );
  }, []);

  const handleDragMove = useCallback((event: DragMoveEvent) => {
    setDropIndicatorId(event.over ? String(event.over.id) : null);
    setDropIndicatorSide(getDropSide(event));
  }, []);

  const resetDragState = useCallback(() => {
    setActiveDragType(null);
    setDropIndicatorId(null);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      resetDragState();
      if (!over) {
        return;
      }

      const activeId = String(active.id);
      const overId = String(over.id);
      const side = getDropSide(event);

      if (
        activeId === PALETTE_DRAG_TYPES.SECTION ||
        activeId === PALETTE_DRAG_TYPES.OPTION ||
        activeId === PALETTE_DRAG_TYPES.FIELD
      ) {
        const drop = resolvePaletteDrop({
          dragType: activeId,
          overId,
          side,
          sections: formDataRef.current.sections,
        });

        if (drop?.kind === 'section') {
          handleAddSectionAt(drop.insertIndex);
        }

        if (drop?.kind === 'option') {
          handleAddOptionAt(drop.sectionIndex, drop.insertIndex);
        }

        if (drop?.kind === 'field') {
          appendSlotToOption(drop.sectionIndex, drop.optionIndex);
        }

        return;
      }

      if (activeId === overId) {
        return;
      }

      // Option reorder / move between sections
      if (activeId.includes(OPTION_DROP_ID_SEPARATOR)) {
        setFormData((prev) => {
          const sectionIds = getSectionIds(prev.sections);
          const activeTarget = getOptionTargetFromDropId({
            sections: prev.sections,
            optionId: activeId,
          });

          if (!activeTarget) {
            return prev;
          }

          const overTarget = overId.includes(OPTION_DROP_ID_SEPARATOR)
            ? getOptionTargetFromDropId({
                sections: prev.sections,
                optionId: overId,
              })
            : null;
          const targetSectionIndex = overTarget
            ? overTarget.sectionIndex
            : sectionIds.indexOf(overId);

          if (targetSectionIndex === -1) {
            return prev;
          }

          const sourceSection = prev.sections[activeTarget.sectionIndex];
          const movingOption =
            sourceSection?.options?.[activeTarget.optionIndex];

          if (!sourceSection || !movingOption) {
            return prev;
          }

          const nextSections = [...prev.sections];
          const nextSourceSection = { ...sourceSection };
          const sourceOptions = [...(nextSourceSection.options || [])];
          sourceOptions.splice(activeTarget.optionIndex, 1);
          nextSourceSection.options = sourceOptions;
          nextSourceSection.template_type = getSectionTemplateType(
            prev.type,
            sourceOptions,
          );
          nextSections[activeTarget.sectionIndex] = nextSourceSection;

          const nextTargetSection =
            activeTarget.sectionIndex === targetSectionIndex
              ? nextSourceSection
              : { ...nextSections[targetSectionIndex] };
          const targetOptions = [...(nextTargetSection.options || [])];

          const rawInsertIndex = overTarget
            ? side === 'before'
              ? overTarget.optionIndex
              : overTarget.optionIndex + 1
            : targetOptions.length;

          const insertIndex = getStrictInsertIndex({
            options: targetOptions,
            insertAt:
              overTarget &&
              activeTarget.sectionIndex === targetSectionIndex &&
              rawInsertIndex > activeTarget.optionIndex
                ? rawInsertIndex - 1
                : rawInsertIndex,
          });

          if (
            activeTarget.sectionIndex === targetSectionIndex &&
            insertIndex === activeTarget.optionIndex
          ) {
            return prev;
          }

          targetOptions.splice(insertIndex, 0, movingOption);
          nextTargetSection.options = targetOptions;
          nextTargetSection.template_type = getSectionTemplateType(
            prev.type,
            targetOptions,
          );
          nextSections[targetSectionIndex] = nextTargetSection;

          setActiveSectionIndex(targetSectionIndex);
          lastFocusedSectionRef.current = targetSectionIndex;
          focusedOptionRef.current = {
            sectionIndex: targetSectionIndex,
            optionIndex: insertIndex,
          };
          setHasFocusedOption(true);

          return { ...prev, sections: nextSections };
        });
        return;
      }

      // Section reorder
      const currentSections = formDataRef.current.sections;
      const sectionIds = getSectionIds(currentSections);
      const oldIndex = sectionIds.indexOf(activeId);
      const newIndex = sectionIds.indexOf(overId);
      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      const newSections = arrayMove(currentSections, oldIndex, newIndex);
      focusedOptionRef.current = remapFocusedOption({
        prevSections: currentSections,
        nextSections: newSections,
        focused: focusedOptionRef.current,
      });
      setFormData((prev) => ({ ...prev, sections: newSections }));
    },
    [appendSlotToOption, handleAddOptionAt, handleAddSectionAt, resetDragState],
  );

  // ============================================================
  // Section operations
  // ============================================================

  const handleSectionsChange = useCallback(
    (sections: DocumentSectionSpec[]) => {
      focusedOptionRef.current = remapFocusedOption({
        prevSections: formDataRef.current.sections,
        nextSections: sections,
        focused: focusedOptionRef.current,
      });
      setFormData((prev) => ({
        ...prev,
        sections: sections.map((section) => {
          const templateType = getSectionTemplateType(
            prev.type,
            section.options,
          );

          return section.template_type === templateType
            ? section
            : { ...section, template_type: templateType };
        }),
      }));
      setErrorSectionIndex(null);
    },
    [],
  );

  const handleRequiredChange = useCallback(
    (sectionIndex: number, required: boolean) => {
      setFormData((prev) => ({
        ...prev,
        sections: prev.sections.map((section, index) =>
          index === sectionIndex ? { ...section, required } : section,
        ),
      }));
    },
    [],
  );

  const handleSaveAiInstructions = useCallback(
    (instructions: string) => {
      const target = aiInstructionsTarget;
      if (!target) {
        return;
      }

      setFormData((prev) => ({
        ...prev,
        sections: prev.sections.map((section, sectionIndex) => {
          if (sectionIndex !== target.sectionIndex) {
            return section;
          }

          if (target.kind === 'section') {
            return { ...section, instructions: instructions || undefined };
          }

          return {
            ...section,
            options: (section.options ?? []).map((option, optionIndex) =>
              optionIndex === target.optionIndex
                ? { ...option, instructions: instructions || undefined }
                : option,
            ),
          };
        }),
      }));
      setAiInstructionsTarget(null);
    },
    [aiInstructionsTarget],
  );

  const handleAddAiOption = useCallback((sectionIndex: number) => {
    const section = formDataRef.current.sections[sectionIndex];
    if (!section || getAiOptionIndex(section) >= 0) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      sections: prev.sections.map((item, index) =>
        index === sectionIndex
          ? {
              ...item,
              options: [...(item.options ?? []), createAiOption()],
              template_type: 'flexible',
            }
          : item,
      ),
    }));
    setActiveSectionIndex(sectionIndex);
    lastFocusedSectionRef.current = sectionIndex;
  }, []);

  const handleEditorFocus = useCallback(
    (sectionIndex: number, optionIndex: number) => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }
      addFieldSelectionRef.current = null;
      focusedOptionRef.current = { sectionIndex, optionIndex };
      lastFocusedSectionRef.current = sectionIndex;
      setActiveSectionIndex(sectionIndex);
      setHasFocusedOption(true);
    },
    [],
  );

  const handleEditorBlur = useCallback(() => {
    // SlotEditor reports one blur twice; a stale timer would clear the focus
    // of the editor that took it over.
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }
    editorSelectionTextRef.current = null;
    blurTimeoutRef.current = setTimeout(() => {
      setHasFocusedOption(false);
    }, 200);
  }, []);

  const handleSlotClick = useCallback(
    (sectionIndex: number, optionIndex: number, slotName: string) => {
      pendingSlotInsertionRef.current = null;
      setSlotEditState({ sectionIndex, optionIndex, slotName });
    },
    [],
  );

  const closeSlotEditor = useCallback(() => {
    pendingSlotInsertionRef.current = null;
    setSlotEditState(null);
  }, []);

  const handleSlotCreate = useCallback(
    (
      sectionIndex: number,
      optionIndex: number,
      slotName: string,
      firstValue: string,
    ) => {
      setFormData((prev) => ({
        ...prev,
        sections: updateOption({
          sections: prev.sections,
          sectionIndex,
          optionIndex,
          update: (option) => ({
            ...option,
            slots: {
              ...(option.slots ?? {}),
              [slotName]: firstValue ? [firstValue] : [],
            },
          }),
        }),
      }));
    },
    [],
  );

  const handleSaveSlotValues = useCallback(
    ({ values, instructions }: SlotEditorSaveResult) => {
      if (!slotEditState) {
        return;
      }

      const { sectionIndex, optionIndex, slotName } = slotEditState;
      const pendingInsertion = pendingSlotInsertionRef.current;
      const trimmedInstructions = instructions.trim();

      setFormData((prev) => ({
        ...prev,
        sections: updateOption({
          sections: prev.sections,
          sectionIndex,
          optionIndex,
          update: (option) => {
            const otherInstructions = Object.fromEntries(
              Object.entries(option.slot_instructions ?? {}).filter(
                ([name]) => name !== slotName,
              ),
            );
            const slotInstructions = trimmedInstructions
              ? { ...otherInstructions, [slotName]: trimmedInstructions }
              : otherInstructions;

            return {
              ...option,
              text: isPendingSlot(pendingInsertion, slotEditState)
                ? replaceTextRangeWithSlot(
                    option.text,
                    slotName,
                    pendingInsertion.start,
                    pendingInsertion.end,
                  )
                : option.text,
              slots: { ...(option.slots ?? {}), [slotName]: values },
              slot_instructions:
                Object.keys(slotInstructions).length > 0
                  ? slotInstructions
                  : undefined,
            };
          },
        }),
      }));
      closeSlotEditor();
    },
    [closeSlotEditor, slotEditState],
  );

  const handleSlotAiToggle = useCallback(
    (nextIsAi: boolean) => {
      if (!slotEditState || isAiSlotName(slotEditState.slotName) === nextIsAi) {
        return;
      }

      const { sectionIndex, optionIndex, slotName } = slotEditState;
      const option =
        formDataRef.current.sections[sectionIndex]?.options?.[optionIndex];
      if (!option) {
        return;
      }

      const occupiedNames = new Set([
        ...extractSlotNames(option.text),
        ...Object.keys(option.slots ?? {}),
      ]);
      occupiedNames.delete(slotName);
      const newName = toggleAiSlotName({ slotName, occupiedNames });
      const pendingInsertion = pendingSlotInsertionRef.current;

      if (isPendingSlot(pendingInsertion, slotEditState)) {
        pendingSlotInsertionRef.current = {
          ...pendingInsertion,
          slotName: newName,
        };
      } else {
        setFormData((prev) => ({
          ...prev,
          sections: updateOption({
            sections: prev.sections,
            sectionIndex,
            optionIndex,
            update: (current) =>
              renameSlotInOption({
                option: current,
                oldName: slotName,
                newName,
              }),
          }),
        }));
      }

      setSlotEditState({ ...slotEditState, slotName: newName });
    },
    [slotEditState],
  );

  // ============================================================
  // Template details
  // ============================================================

  const notifyUnsupportedOptionsRemoved = useCallback(() => {
    toastCaller({
      type: 'warning',
      message: formatMessage(
        templateEditorMessages.unsupportedOptionsRemovedNotice,
      ),
    });
  }, []);

  const handleDetailsSave = useCallback(
    (values: TemplateDetailsValues) => {
      const current = formDataRef.current;
      const nextType = values.documentType ?? current.type;
      // Options are hidden for non-Protocol types, so drop them at the moment
      // the type changes instead of silently losing them on save.
      const strippedSections =
        nextType !== current.type
          ? stripUnsupportedOptions(current.sections, nextType)
          : undefined;

      if (strippedSections?.removedOptions) {
        notifyUnsupportedOptionsRemoved();
      }

      setFormData((prev) => ({
        ...prev,
        name: values.name,
        language: values.language,
        status: values.status,
        type: nextType,
        ...(strippedSections ? { sections: strippedSections.sections } : {}),
      }));
      setSelectedSchemaIds(values.schemaIds);
      setIsDetailsOpen(false);
    },
    [notifyUnsupportedOptionsRemoved],
  );

  // The schemas list may load after the form mounts: derive the membership of
  // an existing template once per template/schemas combination.
  useEffect(() => {
    if (!template?.series_id || !specialtyOptions?.length) {
      return;
    }

    const key = `${template.series_id}:${specialtyOptions
      .map((schema) => schema.id)
      .join(',')}`;
    // A selection the user changed but has not persisted yet wins over the
    // store: re-deriving would silently drop it.
    const isSelectionDirty = !areIdListsEqual(
      selectedSchemaIdsRef.current,
      initialSchemaIdsRef.current,
    );
    if (schemaMembershipKeyRef.current === key || isSelectionDirty) {
      return;
    }

    schemaMembershipKeyRef.current = key;
    const ids = getTemplateSchemaIds({
      schemas: specialtyOptions,
      seriesId: template.series_id,
    });
    setSelectedSchemaIds(ids);
    setInitialSchemaIds(ids);
    originalSchemaIdsRef.current = ids;
  }, [specialtyOptions, template?.series_id]);

  // ============================================================
  // Submit
  // ============================================================

  const getValidationError = useCallback(
    (
      data: Pick<FormData, 'name' | 'code' | 'tab_title'> & {
        type: DocumentType;
        sections: DocumentSectionSpec[];
      },
    ): ValidationIssue | null => {
      if (!data.name.trim()) {
        return {
          message: formatMessage(templateEditorMessages.validationNameRequired),
        };
      }

      if (!data.code.trim()) {
        return {
          message: formatMessage(templateEditorMessages.validationCodeRequired),
        };
      }

      if (!data.tab_title.trim()) {
        return {
          message: formatMessage(
            templateEditorMessages.validationTabTitleRequired,
          ),
        };
      }

      const { allowOptions } = getDocumentTypeRules(data.type);

      for (const [sectionIndex, section] of data.sections.entries()) {
        if (!section.title.trim()) {
          return {
            message: formatMessage(
              templateEditorMessages.validationSectionTitleRequired,
            ),
            sectionIndex,
          };
        }

        if (!section.code.trim()) {
          return {
            message: formatMessage(
              templateEditorMessages.validationSectionCodeRequired,
            ),
            sectionIndex,
          };
        }

        if (
          allowOptions &&
          section.template_type === 'strict' &&
          (section.options?.length ?? 0) === 0
        ) {
          return {
            message: formatMessage(
              templateEditorMessages.validationSectionNeedsOption,
            ),
            sectionIndex,
          };
        }

        for (const option of section.options ?? []) {
          if (!option.text.trim()) {
            return {
              message: formatMessage(
                templateEditorMessages.validationOptionTextRequired,
              ),
              sectionIndex,
            };
          }

          for (const values of Object.values(option.slots ?? {})) {
            if (values.some((value) => value.includes('|'))) {
              return {
                message: formatMessage(
                  templateEditorMessages.validationSlotPipeForbidden,
                ),
                sectionIndex,
              };
            }
          }
        }
      }

      return null;
    },
    [formatMessage],
  );

  const syncSchemaMembership = useCallback(
    async ({
      schemaId,
      templateSeriesId,
      action,
    }: {
      schemaId: string;
      templateSeriesId: string;
      action: 'attach' | 'detach';
    }) => {
      const schema = await templatesApi.getEncounterSchema(schemaId);
      const documents = buildSchemaDocuments({
        schema,
        templateSeriesId,
        action,
      });

      if (!documents) {
        return;
      }

      await dispatch(
        templatesModel.updateEncounterSchemaThunk({
          id: schemaId,
          data: { documents },
        }),
      ).unwrap();
    },
    [],
  );

  const syncTemplateMembership = useCallback(
    async ({
      templateSeriesId,
      silent,
    }: {
      templateSeriesId: string;
      silent: boolean;
    }): Promise<boolean> => {
      const { toAttach, toDetach } = diffSchemaMembership({
        original: originalSchemaIdsRef.current,
        selected: selectedSchemaIdsRef.current,
      });
      const changes = [
        ...toAttach.map((schemaId) => ({
          schemaId,
          action: 'attach' as const,
        })),
        ...toDetach.map((schemaId) => ({
          schemaId,
          action: 'detach' as const,
        })),
      ];
      const results = await Promise.allSettled(
        changes.map(({ schemaId, action }) =>
          syncSchemaMembership({ schemaId, templateSeriesId, action }),
        ),
      );
      const failedIds = changes
        .filter((_, index) => results[index].status === 'rejected')
        .map(({ schemaId }) => schemaId);

      // A failed attach stays out of "original" and a failed detach stays in
      // it, so the next save recomputes the same diff and retries only the
      // failures. The form stays dirty until the membership is persisted.
      originalSchemaIdsRef.current = [
        ...originalSchemaIdsRef.current.filter(
          (id) => !toDetach.includes(id) || failedIds.includes(id),
        ),
        ...toAttach.filter((id) => !failedIds.includes(id)),
      ];
      setInitialSchemaIds(originalSchemaIdsRef.current);

      if (failedIds.length === 0) {
        return true;
      }

      const message = formatMessage(
        templateEditorMessages.specialtyAttachError,
      );

      if (!silent || lastAutoSaveErrorRef.current !== message) {
        toastCaller({ type: 'error', message });
        lastAutoSaveErrorRef.current = message;
      }

      return false;
    },
    [syncSchemaMembership],
  );

  const persistTemplate = useCallback(
    async ({ silent = false }: PersistTemplateOptions = {}) => {
      if (readOnly || isPersistingRef.current) {
        return false;
      }

      const currentFormData = formDataRef.current;
      const snapshot = JSON.stringify(currentFormData);
      const isFormDirty = snapshot !== initialFormDataRef.current;
      const persistedSeriesId =
        activeTemplateRef.current.series_id ?? template?.series_id;

      if (!isFormDirty && persistedSeriesId) {
        try {
          isPersistingRef.current = true;
          setIsPersisting(true);

          const isMembershipSynced = await syncTemplateMembership({
            templateSeriesId: persistedSeriesId,
            silent,
          });
          const persistedTemplateId = activeTemplateRef.current.id;

          // The first autosave of a new template skips its URL update when
          // the membership sync fails; point the URL at the saved version
          // here so a reload does not reopen a blank form.
          if (
            silent &&
            persistedTemplateId &&
            persistedTemplateId !== template?.id
          ) {
            navigateWithoutPrompt(
              buildTemplatesUrl({
                editor: 'document',
                mode: 'view',
                id: persistedTemplateId,
                specialty: initialSchemaId ?? undefined,
              }),
              {
                replace: true,
                state: templatesNavigationState,
              },
            );
          }

          if (!isMembershipSynced) {
            return false;
          }

          lastAutoSaveErrorRef.current = null;

          if (!silent) {
            toastCaller({
              type: 'success',
              message: formatMessage(templateEditorMessages.saveSuccess),
            });

            skipNavigationBlockRef.current = true;
            onSaveComplete?.(activeTemplateRef.current.id ?? '');
            queueMicrotask(() => {
              skipNavigationBlockRef.current = false;
            });
          }

          return true;
        } finally {
          isPersistingRef.current = false;
          setIsPersisting(false);
        }
      }

      const trimmed = trimFields(currentFormData);
      const tabTitle = deriveTabTitle({
        name: trimmed.name,
        tabTitle: trimmed.tab_title,
        persistedName: hydratedNameRef.current,
      });
      const normalizedDocumentType = normalizeDocumentType(trimmed.type);
      // A new template keeps the code of its first saved version instead of
      // regenerating a (possibly random) one on every save.
      const knownCode = trimmed.code || activeTemplateRef.current.code;
      const code = knownCode || generateTemplateCode(trimmed.name);
      const sections = trimmed.sections.map((section) =>
        sanitizeSectionForSubmit(section, normalizedDocumentType),
      );
      const validationError = getValidationError({
        name: trimmed.name,
        code,
        tab_title: tabTitle,
        type: normalizedDocumentType,
        sections,
      });

      if (validationError) {
        if (!silent) {
          toastCaller({
            type: 'error',
            message: validationError.message,
          });
          if (validationError.sectionIndex !== undefined) {
            const targetSectionIndex = validationError.sectionIndex;
            setErrorSectionIndex(targetSectionIndex);
            requestAnimationFrame(() => {
              document
                .querySelector(`[data-section-anchor="${targetSectionIndex}"]`)
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
          } else {
            setErrorSectionIndex(null);
          }
        }
        return false;
      }

      setErrorSectionIndex(null);

      // Legacy Protocol templates may already be Always in a schema; only a
      // switch into Protocol may not create that combination.
      const isChangingIntoProtocol =
        isProtocolTemplate(normalizedDocumentType) &&
        !isProtocolTemplate(activeTemplateRef.current.type);
      const hasProtocolAlwaysMembership =
        isChangingIntoProtocol &&
        !!persistedSeriesId &&
        (specialtyOptions ?? [])
          .filter((schema) => selectedSchemaIdsRef.current.includes(schema.id))
          .some(
            (schema) =>
              normalizeInclusionValue(
                getTemplateInclusion(
                  { series_id: persistedSeriesId, code },
                  schema,
                ),
              ) === 'always' &&
              getInclusionChangeError({
                nextInclusion: 'always',
                schema,
                documentType: normalizedDocumentType,
                templateSeriesId: persistedSeriesId,
              }) === 'protocol_cannot_be_always',
          );

      if (hasProtocolAlwaysMembership) {
        const message = formatMessage(
          templateEditorMessages.protocolCannotBeAlways,
        );

        if (!silent || lastAutoSaveErrorRef.current !== message) {
          toastCaller({ type: 'error', message });
          lastAutoSaveErrorRef.current = message;
        }
        return false;
      }

      const previousTemplateId = activeTemplateRef.current.id;
      const nextStatus = trimmed.status || 'draft';
      // A draft is a working copy: saving it as a draft rewrites the stored
      // version in place. Publishing, or editing a non-draft, appends a
      // version to the series.
      const draftIdToRewrite =
        activeTemplateRef.current.status === 'draft' && nextStatus === 'draft'
          ? previousTemplateId
          : undefined;

      try {
        isPersistingRef.current = true;
        setIsPersisting(true);

        const content = {
          code,
          language: trimmed.language,
          type: normalizedDocumentType,
          name: trimmed.name,
          tab_title: tabTitle,
          description: trimmed.description,
          sections,
          source_document: trimmed.source_document,
        };

        const saveVersion = (versionCode: string) =>
          draftIdToRewrite
            ? dispatch(
                templatesModel.updateDocumentTemplateDraft({
                  id: draftIdToRewrite,
                  data: { ...content, code: versionCode },
                }),
              ).unwrap()
            : dispatch(
                templatesModel.createDocumentTemplateVersion({
                  ...content,
                  code: versionCode,
                  organization_id:
                    trimmed.organization_id ||
                    activeTemplateRef.current.organization_id ||
                    organizationId,
                  status: nextStatus,
                  base_series_id: persistedSeriesId,
                  source_kind: trimmed.source_kind,
                  derived_from_kind: trimmed.derived_from_kind,
                  derived_from_id: trimmed.derived_from_id,
                }),
              ).unwrap();

        // The code field is hidden, so a clash of a generated code is not
        // the user's to fix: retry once with a randomised code.
        const saveWithCodeRetry = async () => {
          try {
            return { result: await saveVersion(code), savedCode: code };
          } catch (error) {
            const conflictFields =
              getDuplicateTemplateSaveConflictFields(error);
            const isGeneratedCodeConflict =
              !knownCode &&
              conflictFields.includes('code') &&
              !conflictFields.includes('name');

            if (!isGeneratedCodeConflict) {
              throw error;
            }

            const retryCode = generateUniqueTemplateCode(trimmed.name);

            return {
              result: await saveVersion(retryCode),
              savedCode: retryCode,
            };
          }
        };

        const { result, savedCode } = await saveWithCodeRetry();

        activeTemplateRef.current = {
          ...getPersistedTemplateState(result),
          code: result.code || savedCode,
          type: normalizedDocumentType,
        };

        if (result.id) {
          pendingPersistedTemplateRef.current = {
            templateId: result.id,
            snapshot,
          };
        }

        syncInitialSnapshot(snapshot);
        lastAutoSaveErrorRef.current = null;

        const isMembershipSynced = result.series_id
          ? await syncTemplateMembership({
              templateSeriesId: result.series_id,
              silent,
            })
          : true;

        if (!silent && isMembershipSynced) {
          toastCaller({
            type: 'success',
            message: formatMessage(templateEditorMessages.saveSuccess),
          });
        }

        const hasLocalChangesAfterSave =
          JSON.stringify(formDataRef.current) !== snapshot;
        const isSaveSettled =
          Boolean(result.id) && !hasLocalChangesAfterSave && isMembershipSynced;

        if (isSaveSettled && result.id) {
          if (!silent) {
            skipNavigationBlockRef.current = true;
            onSaveComplete?.(result.id);
            queueMicrotask(() => {
              skipNavigationBlockRef.current = false;
            });
          } else if (result.id !== previousTemplateId) {
            // Autosave appended a version: point the URL at it. An in-place
            // draft rewrite keeps the id, so the URL is already current.
            navigateWithoutPrompt(
              buildTemplatesUrl({
                editor: 'document',
                mode: 'view',
                id: result.id,
                specialty: initialSchemaId ?? undefined,
              }),
              {
                replace: true,
                state: templatesNavigationState,
              },
            );
          }
        }

        return isMembershipSynced;
      } catch (error) {
        const duplicateConflictFields =
          getDuplicateTemplateSaveConflictFields(error);
        const errorMessage =
          duplicateConflictFields.length > 0
            ? formatMessage(
                duplicateConflictFields.includes('name')
                  ? templateEditorMessages.duplicateTemplateNameError
                  : templateEditorMessages.duplicateTemplateCodeError,
              )
            : getErrorMessage(error) ??
              formatMessage(templateEditorMessages.saveError);

        if (!silent || lastAutoSaveErrorRef.current !== errorMessage) {
          toastCaller({
            type: 'error',
            message: errorMessage,
          });
          lastAutoSaveErrorRef.current = errorMessage;
        }

        return false;
      } finally {
        isPersistingRef.current = false;
        setIsPersisting(false);
      }
    },
    [
      getValidationError,
      initialSchemaId,
      navigateWithoutPrompt,
      onSaveComplete,
      organizationId,
      readOnly,
      specialtyOptions,
      syncInitialSnapshot,
      syncTemplateMembership,
      template?.id,
      template?.series_id,
      templatesNavigationState,
    ],
  );

  const handleSubmit = useCallback(async () => {
    await persistTemplate();
  }, [persistTemplate]);

  useEffect(() => {
    if (readOnly) {
      return;
    }

    const autosaveInterval = window.setInterval(() => {
      const isFormDirty =
        JSON.stringify(formDataRef.current) !== initialFormDataRef.current;
      const isMembershipDirty = !areIdListsEqual(
        selectedSchemaIdsRef.current,
        initialSchemaIdsRef.current,
      );

      if (isPersistingRef.current || (!isFormDirty && !isMembershipDirty)) {
        return;
      }

      void persistTemplate({ silent: true });
    }, 60_000);

    return () => {
      window.clearInterval(autosaveInterval);
    };
  }, [persistTemplate, readOnly]);

  // ============================================================
  // Editor quick actions
  // ============================================================

  const handleClickAddStrictOption = useCallback(() => {
    const sections = formDataRef.current.sections;
    if (sections.length === 0) {
      return;
    }

    const focused = hasFocusedOption ? focusedOptionRef.current : null;
    const targetSectionIndex =
      focused !== null
        ? focused.sectionIndex
        : lastFocusedSectionRef.current !== null
          ? lastFocusedSectionRef.current
          : sections.length - 1;
    const insertAt =
      focused !== null
        ? focused.optionIndex + 1
        : sections[targetSectionIndex]?.options?.length ?? 0;

    handleAddOptionAt(targetSectionIndex, insertAt);
  }, [handleAddOptionAt, hasFocusedOption]);

  const handleAddSlot = useCallback(() => {
    const sections = formDataRef.current.sections;
    const focused = focusedOptionRef.current;
    const focusedOption =
      focused && sections[focused.sectionIndex]?.options?.[focused.optionIndex];
    const selectionSnapshot =
      addFieldSelectionRef.current ?? editorSelectionTextRef.current;
    addFieldSelectionRef.current = null;

    if (focused && focusedOption && selectionSnapshot?.text.trim()) {
      const slotName = getNextSlotName(focusedOption);

      pendingSlotInsertionRef.current = {
        ...selectionSnapshot,
        ...focused,
        slotName,
      };
      setSlotEditState({ ...focused, slotName });
      return;
    }

    if (focused && focusedOption && !focusedOption.text.trim()) {
      appendSlotToOption(focused.sectionIndex, focused.optionIndex);
      return;
    }

    if (focused && focusedOption && editorInsertSlotRef.current) {
      const slotName = getNextSlotName(focusedOption);

      editorInsertSlotRef.current(slotName);
      pendingSlotInsertionRef.current = null;
      setSlotEditState({ ...focused, slotName });
      return;
    }

    if (focused && focusedOption) {
      appendSlotToOption(focused.sectionIndex, focused.optionIndex);
      return;
    }

    const targetSectionIndex = [
      activeSectionIndex,
      lastFocusedSectionRef.current,
    ].find(
      (index): index is number =>
        index !== null && getLastStrictOptionIndex(sections[index]) >= 0,
    );
    const sectionIndex =
      targetSectionIndex ?? findLastSectionWithStrictOptions(sections);

    if (sectionIndex < 0) {
      return;
    }

    appendSlotToOption(
      sectionIndex,
      getLastStrictOptionIndex(sections[sectionIndex]),
    );
  }, [activeSectionIndex, appendSlotToOption]);

  const handleAddFieldPointerDownCapture = useCallback(() => {
    addFieldSelectionRef.current = editorSelectionTextRef.current;
  }, []);

  const handlePromoteOptionToSection = useCallback(
    (sectionIndex: number, optionIndex: number) => {
      const targetSection = formDataRef.current.sections[sectionIndex];
      const option = targetSection?.options?.[optionIndex];

      if (!targetSection || !option || isAiOption(option)) {
        return;
      }

      const newSections = [...formDataRef.current.sections];
      const section = { ...newSections[sectionIndex] };
      const options = [...(section.options || [])];
      options.splice(optionIndex, 1);
      section.options = options;
      section.template_type = getSectionTemplateType(
        formDataRef.current.type,
        options,
      );
      newSections[sectionIndex] = section;

      const nextSection: DocumentSectionSpec = {
        ...createNewSection(formDataRef.current.type),
        title: option.text,
        instructions: option.instructions,
      };

      newSections.splice(sectionIndex + 1, 0, nextSection);

      setFormData((prev) => ({ ...prev, sections: newSections }));
      setActiveSectionIndex(sectionIndex + 1);
      lastFocusedSectionRef.current = sectionIndex + 1;
      focusedOptionRef.current = null;
      setHasFocusedOption(false);
      setAutoFocusTarget({ sectionIndex: sectionIndex + 1 });
      scrollToSection(sectionIndex + 1);
    },
    [scrollToSection],
  );

  const handleDemoteSectionToOption = useCallback(
    (sectionIndex: number) => {
      if (sectionIndex <= 0) {
        return;
      }

      const section = formDataRef.current.sections[sectionIndex];
      // New sections carry the default AI option, which must not block the
      // demotion of an otherwise empty section; a prompt written for it would
      // be lost, so such a section stays a section.
      if (
        !section ||
        getLastStrictOptionIndex(section) >= 0 ||
        section.options?.some(
          (option) => isAiOption(option) && option.instructions?.trim(),
        )
      ) {
        return;
      }

      const previousSection = formDataRef.current.sections[sectionIndex - 1];
      if (!previousSection) {
        return;
      }

      const newSections = [...formDataRef.current.sections];
      const newPreviousSection = { ...previousSection };
      const previousOptions = [...(newPreviousSection.options || [])];
      const nextOptionIndex = getStrictInsertIndex({
        options: previousOptions,
        insertAt: previousOptions.length,
      });
      previousOptions.splice(nextOptionIndex, 0, {
        text: section.title,
        instructions: section.instructions,
      });
      newPreviousSection.options = previousOptions;
      newPreviousSection.template_type = getSectionTemplateType(
        formDataRef.current.type,
        previousOptions,
      );
      newSections[sectionIndex - 1] = newPreviousSection;
      newSections.splice(sectionIndex, 1);

      setFormData((prev) => ({ ...prev, sections: newSections }));
      setActiveSectionIndex(sectionIndex - 1);
      lastFocusedSectionRef.current = sectionIndex - 1;
      focusedOptionRef.current = {
        sectionIndex: sectionIndex - 1,
        optionIndex: nextOptionIndex,
      };
      setHasFocusedOption(true);
      setAutoFocusTarget({
        sectionIndex: sectionIndex - 1,
        optionIndex: nextOptionIndex,
      });
      scrollToSection(sectionIndex - 1);
    },
    [scrollToSection],
  );

  const handleSelectSection = useCallback(
    (sectionIndex: number) => {
      setActiveSectionIndex(sectionIndex);
      lastFocusedSectionRef.current = sectionIndex;
      focusedOptionRef.current = null;
      setHasFocusedOption(false);
      scrollToSection(sectionIndex);
    },
    [scrollToSection],
  );

  const handleActiveSectionChange = useCallback((sectionIndex: number) => {
    setActiveSectionIndex(sectionIndex);
    lastFocusedSectionRef.current = sectionIndex;
  }, []);

  const handleSectionFocusFromEditor = useCallback((sectionIndex: number) => {
    setActiveSectionIndex(sectionIndex);
    lastFocusedSectionRef.current = sectionIndex;
  }, []);

  const handleExport = useCallback(() => {
    const data = formDataRef.current;
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const filenameBase = data.code || data.name || 'template';
    const language = data.language ? `_${data.language}` : '';
    link.download = `${filenameBase}${language}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  const autoImportTriggeredRef = useRef(false);
  useEffect(() => {
    if (autoImportTriggeredRef.current || !autoOpenImport) {
      return;
    }
    autoImportTriggeredRef.current = true;

    if (formDataRef.current.source_document) {
      setIsSourceDocOpen(true);
    } else {
      setIsImportOpen(true);
    }
  }, [autoOpenImport]);

  const handleImportModalClose = useCallback(() => {
    setIsImportOpen(false);
    // A fresh template still needs its name — hand over to the details modal.
    if (
      !formDataRef.current.name.trim() &&
      !formDataRef.current.sections.length
    ) {
      setIsDetailsOpen(true);
    }
  }, []);

  const handleSaveSourceDocument = useCallback((sourceDocument: string) => {
    setFormData((prev) => ({ ...prev, source_document: sourceDocument }));
    setIsSourceDocOpen(false);
  }, []);

  const applyImportedSections = useCallback(
    (
      imported: Partial<FormData>,
      importedSections: DocumentSectionSpec[],
      sourceContent: string,
      strategy: ImportStrategy,
    ): void => {
      const current = formDataRef.current;
      const nextType =
        strategy === 'append' ? current.type : imported.type ?? current.type;
      const mergedSections =
        strategy === 'append'
          ? [...current.sections, ...importedSections]
          : importedSections.length > 0
            ? importedSections
            : current.sections;
      // Imported files may carry options the target type cannot show; drop
      // them here so the editor and the saved draft agree.
      const { sections: supportedSections, removedOptions } =
        stripUnsupportedOptions(mergedSections, nextType);
      const sections = supportedSections.map(addLegacyAiOption);

      if (removedOptions) {
        notifyUnsupportedOptionsRemoved();
      }

      setFormData((prev) => {
        if (strategy === 'append') {
          return {
            ...prev,
            name: prev.name || imported.name || '',
            code: prev.code || imported.code || '',
            tab_title: prev.tab_title || imported.tab_title || '',
            description: prev.description || imported.description,
            sections,
            source_document: prev.source_document
              ? `${prev.source_document}\n\n${sourceContent}`
              : sourceContent,
          };
        }

        return {
          ...prev,
          name: imported.name || prev.name,
          code: imported.code || prev.code,
          language: imported.language || prev.language,
          type: nextType,
          tab_title: imported.tab_title || prev.tab_title,
          description: imported.description ?? prev.description,
          sections,
          source_document: sourceContent,
        };
      });
      setActiveSectionIndex(
        importedSections.length > 0 ? 0 : activeSectionIndex,
      );
      lastFocusedSectionRef.current = importedSections.length > 0 ? 0 : null;
      focusedOptionRef.current = null;
      setIsImportOpen(false);
    },
    [activeSectionIndex, notifyUnsupportedOptionsRemoved],
  );

  const handleParsed = useCallback(
    (
      result: DocumentTemplateParseResponse,
      sourceContent: string,
      strategy: ImportStrategy,
    ): void => {
      applyImportedSections(
        {
          name: result.name,
          code: result.code,
          language: result.language,
          tab_title: result.tab_title,
          description: result.description,
        },
        mapParsedSections(result.sections),
        sourceContent,
        strategy,
      );
    },
    [applyImportedSections],
  );

  const applyImportedJson = useCallback(
    (content: string, strategy: ImportStrategy) => {
      const imported = JSON.parse(content) as ImportedTemplateJson;
      const importedSections: DocumentSectionSpec[] = (
        imported.sections ?? []
      ).map((section) => ({
        code: section.code || generateSectionCode(),
        title: section.title || '',
        category: section.category,
        editor_type: 'section',
        instructions: section.instructions ?? undefined,
        options:
          section.options && section.options.length > 0
            ? section.options
            : undefined,
        type: section.type,
        processing_type: section.processing_type,
        template_type: section.template_type,
        required: section.required,
      }));

      applyImportedSections(
        {
          name: imported.name,
          code: imported.code,
          language: imported.language,
          type: imported.type
            ? normalizeDocumentType(imported.type)
            : undefined,
          tab_title: imported.tab_title,
          description: imported.description,
        },
        importedSections,
        content,
        strategy,
      );
    },
    [applyImportedSections],
  );

  const handleApplyImportedJson = useCallback(
    (content: string, strategy: ImportStrategy): boolean => {
      try {
        applyImportedJson(content, strategy);
        return true;
      } catch (error) {
        debug('Failed to apply imported template JSON', error);
        return false;
      }
    },
    [applyImportedJson],
  );

  // Clone handler for system templates
  const handleCloneToOrganization = useCallback(async () => {
    if (!template || !template.id) {
      return;
    }

    if (!organizationId) {
      toastCaller({
        type: 'error',
        message: formatMessage(templateEditorMessages.cloneError),
      });
      return;
    }

    try {
      const result = await cloneSystemTemplateForEditing({
        template,
        organizationId,
        schemaId: initialSchemaId ?? undefined,
      });

      toastCaller({
        type: 'success',
        message: formatMessage(templateEditorMessages.cloneSuccess),
      });

      if (result.createdTemplate.id) {
        navigateWithoutPrompt(
          buildTemplatesUrl({
            editor: 'document',
            mode: 'edit',
            id: result.createdTemplate.id,
            specialty: initialSchemaId ?? undefined,
          }),
          {
            state: templatesNavigationState,
          },
        );
      }
    } catch (error) {
      toastCaller({
        type: 'error',
        message:
          error instanceof Error && error.message
            ? error.message
            : formatMessage(templateEditorMessages.cloneError),
      });
    }
  }, [
    template,
    organizationId,
    formatMessage,
    navigateWithoutPrompt,
    initialSchemaId,
    templatesNavigationState,
  ]);

  const { allowOptions } = getDocumentTypeRules(formData.type);

  const hasAnyOptions = formData.sections.some(
    (section) => getLastStrictOptionIndex(section) >= 0,
  );

  const slotEditSlotName = slotEditState?.slotName;
  const slotEditOption = slotEditState
    ? formData.sections[slotEditState.sectionIndex]?.options?.[
        slotEditState.optionIndex
      ]
    : undefined;
  const pendingSlotInsertion = pendingSlotInsertionRef.current;
  const slotEditValues = slotEditSlotName
    ? slotEditOption?.slots?.[slotEditSlotName] ??
      (isPendingSlot(pendingSlotInsertion, slotEditState) &&
      pendingSlotInsertion.text
        ? [pendingSlotInsertion.text]
        : [])
    : [];
  const slotEditInstructions = slotEditSlotName
    ? slotEditOption?.slot_instructions?.[slotEditSlotName] ?? ''
    : '';
  const trimmedName = formData.name.trim();

  const selectedSchemas = (specialtyOptions ?? []).filter((schema) =>
    selectedSchemaIds.includes(schema.id),
  );
  const selectedSchemaInclusion =
    template && selectedSchemas[0]
      ? getTemplateInclusion(template, selectedSchemas[0])
      : undefined;

  // ============================================================
  // Render
  // ============================================================

  return (
    <div className={cn(styles.container, className)}>
      <EditorHeader
        title={
          trimmedName ||
          (template
            ? formatMessage(templateEditorMessages.editTemplate)
            : formatMessage(templateEditorMessages.createTemplate))
        }
        readOnly={readOnly}
        canImport={!readOnly}
        canExport={formData.sections.length > 0}
        canSave={Boolean(trimmedName) && !isPersisting}
        isSystem={readOnly}
        checklist={
          hasCompletedChecklist(template) && template ? (
            <div ref={checklistAnchorRef} className={styles.checklistAnchor}>
              <EditorButton
                variant="hairline"
                aria-expanded={isChecklistOpen}
                onClick={() => setIsChecklistOpen((isOpen) => !isOpen)}
              >
                {formatMessage(templateEditorMessages.checklistButton)}
              </EditorButton>
              {isChecklistOpen && (
                <ChecklistPanel
                  className={styles.checklistPanel}
                  tabs={[
                    {
                      id: template.id,
                      label: template.tab_title || template.name,
                      rows: buildChecklistRows(template),
                    },
                  ]}
                  triggerRef={checklistAnchorRef}
                  onClose={() => setIsChecklistOpen(false)}
                />
              )}
            </div>
          ) : null
        }
        onBack={() => navigate(templatesBackPath)}
        onEditDetails={() => setIsDetailsOpen(true)}
        onImport={() => setIsImportOpen(true)}
        onExport={handleExport}
        onSave={handleSubmit}
        onClone={handleCloneToOrganization}
      />

      <TemplateMetaLine
        schemaNames={selectedSchemas.map((schema) => schema.name)}
        language={formData.language}
        documentType={formData.type}
        inclusion={selectedSchemaInclusion}
        description={formData.description}
      />

      <TemplateDetailsModal
        isOpen={isDetailsOpen}
        values={{
          name: formData.name,
          language: formData.language,
          status: (formData.status as VersionStatus) || 'draft',
          schemaIds: selectedSchemaIds,
          documentType: formData.type,
        }}
        specialtyOptions={specialtyOptions ?? []}
        languageLocked={Boolean(schemaLanguage)}
        readOnly={readOnly}
        onSave={handleDetailsSave}
        onClose={() => setIsDetailsOpen(false)}
      />

      <DndContext
        sensors={sensors}
        collisionDetection={dndCollisionDetection}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
        onDragCancel={resetDragState}
      >
        <div className={styles.body}>
          <div className={styles.sidebar}>
            {!readOnly && (
              <EditorToolbar
                allowOptions={allowOptions}
                hasSections={formData.sections.length > 0}
                hasAnyOptions={hasAnyOptions}
                onAddSection={handleClickAddSection}
                onAddStrictOption={handleClickAddStrictOption}
                onAddField={handleAddSlot}
                onAddFieldPointerDownCapture={handleAddFieldPointerDownCapture}
              />
            )}
            <SectionNav
              className={styles.sectionNav}
              sections={formData.sections}
              activeSectionIndex={activeSectionIndex}
              onSelect={handleSelectSection}
            />
          </div>

          <div className={styles.editorPane}>
            {!readOnly && (
              <EditorHistory
                className={styles.history}
                canUndo={canUndo}
                canRedo={canRedo}
                onUndo={handleUndo}
                onRedo={handleRedo}
              />
            )}

            <div ref={editorBodyRef} className={styles.editor}>
              <TemplateSectionsEditor
                sections={formData.sections}
                readOnly={readOnly}
                allowOptions={allowOptions}
                activeSectionIndex={activeSectionIndex}
                autoFocusTarget={autoFocusTarget}
                activeDragType={activeDragType}
                dropIndicatorId={dropIndicatorId}
                dropIndicatorSide={dropIndicatorSide}
                errorSectionIndex={errorSectionIndex}
                editorInsertSlotRef={editorInsertSlotRef}
                editorSelectionTextRef={editorSelectionTextRef}
                onSectionsChange={handleSectionsChange}
                onRequiredChange={handleRequiredChange}
                onOpenSectionAiInstructions={(sectionIndex) =>
                  setAiInstructionsTarget({ kind: 'section', sectionIndex })
                }
                onOpenOptionAiInstructions={(sectionIndex, optionIndex) =>
                  setAiInstructionsTarget({
                    kind: 'option',
                    sectionIndex,
                    optionIndex,
                  })
                }
                onSlotClick={handleSlotClick}
                onSlotCreate={handleSlotCreate}
                onAddAiOption={handleAddAiOption}
                onEditorFocus={handleEditorFocus}
                onEditorBlur={handleEditorBlur}
                onClickAddSection={handleClickAddSection}
                onAddSectionAt={handleAddSectionAt}
                onAddOptionAt={handleAddOptionAt}
                onPromoteOptionToSection={handlePromoteOptionToSection}
                onDemoteSectionToOption={handleDemoteSectionToOption}
                onActiveSectionChange={handleActiveSectionChange}
                onSectionFocus={handleSectionFocusFromEditor}
              />
            </div>
          </div>
        </div>
      </DndContext>

      <AiInstructionsModal
        isOpen={aiInstructionsTarget !== null}
        target={aiInstructionsTarget}
        instructions={getAiInstructionsValue(
          formData.sections,
          aiInstructionsTarget,
        )}
        onSave={handleSaveAiInstructions}
        onClose={() => setAiInstructionsTarget(null)}
      />

      <SlotEditorPopup
        isOpen={slotEditState !== null}
        slotName={slotEditState?.slotName ?? ''}
        values={slotEditValues}
        instructions={slotEditInstructions}
        onSave={handleSaveSlotValues}
        onToggleAi={handleSlotAiToggle}
        onClose={closeSlotEditor}
      />

      <ImportTemplateModal
        isOpen={isImportOpen}
        hasExistingSections={formData.sections.length > 0}
        onApplyJson={handleApplyImportedJson}
        onClose={handleImportModalClose}
        onParsed={handleParsed}
      />

      <SourceDocumentDialog
        isOpen={isSourceDocOpen}
        sourceDocument={formData.source_document ?? undefined}
        onSave={handleSaveSourceDocument}
        onClose={() => setIsSourceDocOpen(false)}
      />
    </div>
  );
};
